import React from "react";
import styled from "styled-components";
import { useTranslation } from "react-i18next";

export interface Insurer {
  id: string;
  name: string;
  width: string;
  height: string;
  src?: string;
}

export interface InsurerSectionProps {
  insurers?: Insurer[];
  className?: string;
}

export const DEFAULT_INSURERS: Insurer[] = [
  {
    id: "sunday",
    name: "Sunday",
    width: "102.85px",
    height: "44px",
    src: "/static/company-logo/logo-sunday.svg",
  },
  {
    id: "viriyah",
    name: "Viriyah",
    width: "48.82px",
    height: "48px",
    src: "/static/company-logo/logo-viriyah.svg",
  },
  {
    id: "allianz",
    name: "Allianz",
    width: "52.15px",
    height: "48px",
    src: "/static/company-logo/logo-allianz.svg",
  },
  {
    id: "ayudhya",
    name: "Ayudhya",
    width: "106.03px",
    height: "28px",
    src: "/static/company-logo/logo-ayudhya.svg",
  },
  {
    id: "axa",
    name: "Axa",
    width: "52px",
    height: "48px",
    src: "/static/company-logo/logo-axa.svg",
  },
  {
    id: "bangkok-insurance",
    name: "Bangkok Insurance",
    width: "114.4px",
    height: "24px",
    src: "/static/company-logo/logo-bangkokinsurance.svg",
  },
  {
    id: "chubb",
    name: "Chubb",
    width: "112.97px",
    height: "12px",
    src: "/static/company-logo/logo-chubb.svg",
  },
  {
    id: "dhipaya",
    name: "Dhipaya",
    width: "52.15px",
    height: "48px",
    src: "/static/company-logo/logo-dhipaya.svg",
  },
  {
    id: "kpi",
    name: "KPI",
    width: "143px",
    height: "42px",
    src: "/static/company-logo/logo-kpi.svg",
  },
  {
    id: "lmg",
    name: "LMG",
    width: "134.03px",
    height: "50px",
    src: "/static/company-logo/logo-lmg.svg",
  },
  {
    id: "msig",
    name: "MSIG",
    width: "86.27px",
    height: "46px",
    src: "/static/company-logo/logo-msig.svg",
  },
  {
    id: "muang-thai",
    name: "Muang Thai",
    width: "48px",
    height: "48px",
    src: "/static/company-logo/logo-muangthai.svg",
  },
  {
    id: "pacific-cross",
    name: "Pacific Cross",
    width: "74.4px",
    height: "44px",
    src: "/static/company-logo/logo-pasificcross.svg",
  },
  {
    id: "sompo",
    name: "Sompo",
    width: "93.2px",
    height: "32px",
    src: "/static/company-logo/logo-sompo.svg",
  },
  {
    id: "thai-sri",
    name: "Thai Sri",
    width: "93.33px",
    height: "28px",
    src: "/static/company-logo/logo-thaisri.svg",
  },
  {
    id: "thai-vivat",
    name: "Thai Vivat",
    width: "48.82px",
    height: "48px",
    src: "/static/company-logo/logo-thaivivat.svg",
  },
  {
    id: "tokyo-marine",
    name: "Tokyo Marine",
    width: "123.04px",
    height: "44px",
    src: "/static/company-logo/logo-tokyomarine.svg",
  },
  {
    id: "tune",
    name: "Tune",
    width: "60.84px",
    height: "56px",
    src: "/static/company-logo/logo-tune.svg",
  },
  {
    id: "tlife",
    name: "TLife",
    width: "140.55px",
    height: "32px",
    src: "/static/company-logo/logo-tlife.svg",
  },
  {
    id: "krungthai-axa",
    name: "Krungthai AXA",
    width: "101.28px",
    height: "48px",
    src: "/static/company-logo/logo-krungthaiaxa.svg",
  },
  {
    id: "prudential",
    name: "Prudential",
    width: "92.16px",
    height: "48px",
    src: "/static/company-logo/logo-prudential.svg",
  },
  {
    id: "rabbit-life",
    name: "Rabbit Life",
    width: "110.26px",
    height: "32px",
    src: "/static/company-logo/logo-rabbitlife.svg",
  },
  {
    id: "selife",
    name: "SeLife",
    width: "78.33px",
    height: "32px",
    src: "/static/company-logo/logo-selife.svg",
  },
  {
    id: "tip-life",
    name: "Tip Life",
    width: "85px",
    height: "40px",
    src: "/static/company-logo/logo-tiplife.svg",
  },
];

const SectionContainer = styled.section`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  max-width: 1388px;
  width: 100%;
  margin: 0 auto;
  padding: 80px 0;
  gap: 40px;
  box-sizing: border-box;

  @media (max-width: 1440px) {
    padding: 80px 24px;
  }

  @media (max-width: 768px) {
    padding: 40px 16px;
    gap: 24px;
  }
`;

const SectionTitle = styled.h2`
  font-size: 28px;
  line-height: 36px;
  font-weight: 600;
  color: #202123;
  text-align: center;
  margin: 0;

  @media (max-width: 768px) {
    font-size: 20px;
    line-height: 28px;
  }
`;

const LogoGrid = styled.div`
  display: flex;
  flex-direction: row;
  flex-wrap: wrap;
  justify-content: center;
  align-items: center;
  gap: 16px 32px;
  width: 100%;

  @media (max-width: 768px) {
    gap: 16px;
    width: 343px;
    height: 184.86px;
    align-content: center;
  }
`;

const LogoItemWrapper = styled.div<{ $width: string; $height: string }>`
  display: flex;
  justify-content: center;
  align-items: center;
  width: ${(props) => props.$width};
  height: ${(props) => props.$height};
  flex-shrink: 0;
  position: relative;

  @media (max-width: 768px) {
    max-width: calc(50% - 16px);

    /* 1. Sunday */
    &.logo-sunday {
      order: 0;
      width: 51.42px;
      height: 22px;
      flex: none;
      flex-grow: 0;
    }

    /* 2. Viriyah */
    &.logo-viriyah {
      order: 1;
      width: 24.41px;
      height: 24px;
      flex: none;
      flex-grow: 0;
    }

    /* 3. Allianz */
    &.logo-allianz {
      order: 2;
      width: 26.07px;
      height: 24px;
      flex: none;
      flex-grow: 0;
    }

    /* 4. Ayudhya */
    &.logo-ayudhya {
      order: 3;
      width: 53.02px;
      height: 14px;
      flex: none;
      flex-grow: 0;
    }

    /* 5. Axa */
    &.logo-axa {
      order: 4;
      width: 26px;
      height: 24px;
      flex: none;
      flex-grow: 0;
    }

    /* 6. Bangkok Insurance */
    &.logo-bangkok-insurance {
      order: 5;
      width: 57.2px;
      height: 12px;
      flex: none;
      flex-grow: 0;
    }

    /* 7. Chubb */
    &.logo-chubb {
      order: 6;
      width: 56.48px;
      height: 6px;
      flex: none;
      flex-grow: 0;
    }

    /* 8. Dhipaya */
    &.logo-dhipaya {
      order: 7;
      width: 26.07px;
      height: 24px;
      flex: none;
      flex-grow: 0;
    }

    /* 9. KPI */
    &.logo-kpi {
      order: 8;
      width: 71.5px;
      height: 21px;
      flex: none;
      flex-grow: 0;
    }

    /* 10. LMG */
    &.logo-lmg {
      order: 9;
      width: 67.01px;
      height: 25px;
      flex: none;
      flex-grow: 0;
    }

    /* 11. MSIG */
    &.logo-msig {
      order: 10;
      width: 43.14px;
      height: 23px;
      flex: none;
      flex-grow: 0;
    }

    /* 12. Muang Thai */
    &.logo-muangthai {
      order: 11;
      width: 24px;
      height: 24px;
      flex: none;
      flex-grow: 0;
    }

    /* 13. Pacific Cross */
    &.logo-pacificcross {
      order: 12;
      width: 37.2px;
      height: 22px;
      flex: none;
      flex-grow: 0;
    }

    /* 14. Sompo */
    &.logo-sompo {
      order: 13;
      width: 46.6px;
      height: 16px;
      flex: none;
      flex-grow: 0;
    }

    /* 15. Thai Sri */
    &.logo-thaisri {
      order: 14;
      width: 46.67px;
      height: 14px;
      flex: none;
      flex-grow: 0;
    }

    /* 16. Thai Vivat */
    &.logo-thaivivat {
      order: 15;
      width: 24.41px;
      height: 24px;
      flex: none;
      flex-grow: 0;
    }

    /* 17. Tokyo Marine */
    &.logo-tokyomarine {
      order: 16;
      width: 61.52px;
      height: 22px;
      flex: none;
      flex-grow: 0;
    }

    /* 18. Tune */
    &.logo-tune {
      order: 17;
      width: 30.42px;
      height: 28px;
      flex: none;
      flex-grow: 0;
    }

    /* 19. T Life */
    &.logo-tlife {
      order: 18;
      width: 70.27px;
      height: 16px;
      flex: none;
      flex-grow: 0;
    }

    /* 20. Krungthai AXA */
    &.logo-krungthaiaxa {
      order: 19;
      width: 50.64px;
      height: 24px;
      flex: none;
      flex-grow: 0;
    }

    /* 21. Prudential */
    &.logo-prudential {
      order: 20;
      width: 46.08px;
      height: 24px;
      flex: none;
      flex-grow: 0;
    }

    /* 22. Rabbit Life */
    &.logo-rabbitlife {
      order: 21;
      width: 55.13px;
      height: 16px;
      flex: none;
      flex-grow: 0;
    }

    /* 23. SE Life */
    &.logo-selife {
      order: 22;
      width: 39.17px;
      height: 16px;
      flex: none;
      flex-grow: 0;
    }

    /* 24. TIP Life */
    &.logo-tiplife {
      order: 23;
      width: 42.5px;
      height: 19.86px;
      flex: none;
      flex-grow: 0;
    }
  }
`;

const LogoImage = styled.img`
  width: 100%;
  height: 100%;
  object-fit: contain;
`;

const LogoPlaceholder = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 100%;
  height: 100%;
  background: #f7f8f9;
  border: 1px dashed #d9dbde;
  border-radius: 6px;
  font-size: 11px;
  font-weight: 500;
  color: #888d92;
  text-align: center;
  box-sizing: border-box;
  padding: 4px;
  user-select: none;
`;

const getLogoClassName = (id: string) => {
  switch (id) {
    case "muang-thai":
      return "logo-muangthai";
    case "pacific-cross":
      return "logo-pacificcross";
    case "thai-sri":
      return "logo-thaisri";
    case "thai-vivat":
      return "logo-thaivivat";
    case "tokyo-marine":
      return "logo-tokyomarine";
    case "krungthai-axa":
      return "logo-krungthaiaxa";
    case "rabbit-life":
      return "logo-rabbitlife";
    case "tip-life":
      return "logo-tiplife";
    default:
      return `logo-${id}`;
  }
};

export const InsurerSection: React.FC<InsurerSectionProps> = ({
  insurers = DEFAULT_INSURERS,
  className,
}) => {
  const { t } = useTranslation("icth-retail-main");
  return (
    <SectionContainer className={className}>
      <SectionTitle>{t("insurer.title")}</SectionTitle>
      <LogoGrid>
        {insurers.map((insurer) => (
          <LogoItemWrapper
            key={insurer.id}
            className={getLogoClassName(insurer.id)}
            $width={insurer.width}
            $height={insurer.height}
          >
            {insurer.src ? (
              <LogoImage
                src={insurer.src}
                alt={`${insurer.name} logo`}
                loading="lazy"
              />
            ) : (
              <LogoPlaceholder>{insurer.name}</LogoPlaceholder>
            )}
          </LogoItemWrapper>
        ))}
      </LogoGrid>
    </SectionContainer>
  );
};

const val: any = () => null;
val.isRequired = () => null;

InsurerSection.propTypes = {
  insurers: val,
  className: val,
};

InsurerSection.defaultProps = {
  insurers: DEFAULT_INSURERS,
  className: undefined,
};

export default InsurerSection;
